/**
 * The single action block of a task page. Rendered only for the role that owns the current
 * stage; everyone else just sees the status line. The backend decides which actions exist and
 * what blocks them; this component shows the blockers as a checklist and posts the transition.
 */
import { useState } from 'react'
import { api, ApiError } from '@/api/client'
import { stageDef } from '@/lib/stages'
import type { AvailableAction, Role, Task, TaskDetail } from '@/lib/types'

interface Props { blockedReason?: string; disabled?: boolean; task: Task; actions: AvailableAction[]; role: Role; onDone: (detail: TaskDetail) => void }

export function NextStep({ task, actions, role, onDone, blockedReason, disabled = false }: Props) {
  const [feedback, setFeedback] = useState('')
  const [acknowledged, setAcknowledged] = useState('')
  const staleDraft = task.stage === 0 && task.coach_review?.stale
  const draftHash = task.coach_review?.current_fingerprint ?? ''
  const [showBack, setShowBack] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [serverBlockers, setServerBlockers] = useState<string[]>([])

  const def = stageDef(task.stage, task.workflow_version)
  if (def.owner !== role || !def.forward) return null
  const forward = actions.find((a) => a.action === 'forward')
  const back = actions.find((a) => a.action === 'send_back')
  if (!forward) return null
  const ready = forward.blockers.length === 0 && !blockedReason
  // Answering the authoring agent's question resumes the build directly, without peer review.
  const copy = task.stage === 2 && forward.to_stage === 3 ? { title: 'Send your answer to the agent?', button: 'Send answer and resume the build' } : def.forward
  // Quality check is the merge gate: say so before the button, not after.
  const submission = task.stage === 4 ? task.authoring_submission : undefined
  async function run(action: AvailableAction) {
    if (disabled) return
    setBusy(true); setError(null); setServerBlockers([])
    try {
      const detail = await api.post<TaskDetail>(`/tasks/${task.task_id}/transition`, { revision: task.revision ?? 0, action: action.action, feedback: feedback || undefined, ...(staleDraft ? { draft_acknowledgement: acknowledged } : {}) })
      setFeedback(''); setShowBack(false)
      onDone(detail)
    } catch (e) {
      if (e instanceof ApiError) { setError(e.message); setServerBlockers(e.blockers) } else setError((e as Error).message)
    } finally { setBusy(false) }
  }

  return (
    <section className={`next-step ${ready ? 'ready' : ''}`}>
      <div className="eyebrow">Next step</div>
      <h2>{copy.title}</h2>
      {ready
        ? !staleDraft && <p className="ok-text">Everything is in place.</p>
        : <><p className="muted">Before you can continue:</p><ul className="checklist">{blockedReason && <li>{blockedReason}</li>}{forward.blockers.map((b, i) => <li key={i}>{b}</li>)}</ul></>}
      {submission && !submission.merged && (
        <p className="merge-notice" role="note">
          Passing quality check merges <a href={submission.pull_request.url} target="_blank" rel="noreferrer">
          pull request #{submission.pull_request.number}</a> into the task dataset.
          Review the diff before you continue.
        </p>
      )}
      {submission?.merged && (
        <p className="muted small">
          Pull request #{submission.pull_request.number} is merged.
        </p>
      )}
      {staleDraft && <label className="inline-check draft-acknowledgement">
        <input type="checkbox" checked={acknowledged === draftHash} onChange={e => setAcknowledged(e.target.checked ? draftHash : '')} />
        Continue with my changes since the coach review
      </label>}
      <div className="row">
        <button className="primary" disabled={disabled || busy || !ready || (!!staleDraft && acknowledged !== draftHash)} onClick={() => run(forward)}>{copy.button}</button>
        {back && def.sendBack && !showBack && <button className="danger" disabled={busy} onClick={() => setShowBack(true)}>{def.sendBack.button}…</button>}
      </div>
      {back && def.sendBack && showBack && (
        <div className="sendback">
          <label className="field">
            <span>{def.sendBack.button}</span>
            <em className="hint">{def.sendBack.prompt}</em>
            <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} autoFocus />
          </label>
          {back.blockers.length > 0 && <p className="muted small">{back.blockers.join(' ')}</p>}
          <div className="row">
            <button className="danger" disabled={busy || back.blockers.length > 0 || !feedback.trim()} onClick={() => run(back)}>{def.sendBack.button}</button>
            <button className="ghost" onClick={() => { setShowBack(false); setFeedback('') }}>Cancel</button>
          </div>
        </div>
      )}
      {error && <div className="note err" style={{ marginTop: '1rem' }}>{error}{serverBlockers.length > 0 && <ul>{serverBlockers.map((b, i) => <li key={i}>{b}</li>)}</ul>}</div>}
    </section>
  )
}
